import { defineStore } from 'pinia'
import { type Ref, ref } from 'vue'
import { axiosClient } from '@/utils/axios-client'
import type {
  CreateApiLocation,
  CreateLocalLocation,
  Location,
  LocationCategory,
  LocationResponse,
  LocationType,
} from '@/model/location.model.ts'
import { decodeLocation } from '@/utils/html-decoder'

export type JourneyLocationType = 'origin' | 'destination'

const urlMap = {
  origin: 'verzendadres',
  destination: 'bestemmingsadres',
} as const

export const useLocationStore = defineStore('location', () => {
  const locations = ref<Location[]>([])
  const locationCategories: Ref<LocationCategory[]> = ref<LocationCategory[]>([])
  const locationTypes: Ref<LocationType[]> = ref<LocationType[]>([
    { value: 'BEIDE' },
    { value: 'VERZENDLOCATIE' },
    { value: 'VERWERKERSLOCATIE' },
  ])
  let loadingCategories: boolean = false

  async function fetchLocationsForOrganization(
    organizationId: number,
    locationType: JourneyLocationType,
    refresh = false
  ) {
    // Prevent over fetchting
    // TODO - make it less jank
    const organizationHasLocations =
      locations.value.find(location => location.organisatie.id == organizationId) != null
    if (organizationHasLocations && !refresh) {
      return
    }
    const url = `/afvalstroom/api/locatie/search/${urlMap[locationType]}?organisatie=${organizationId}`
    const response = await axiosClient.request<Location[]>({ url: url, method: 'GET' })

    locations.value = [...response.data.map(decodeLocation)]
  }

  async function fetchLocationCategories() {
    if (loadingCategories || locationCategories.value.length > 0) {
      return
    }
    loadingCategories = true
    const response = await axiosClient.request<LocationCategory[]>({
      url: '/afvalstroom/api/locatie/types',
      method: 'GET',
    })

    locationCategories.value = response.data
    loadingCategories = false
  }

  // ToDo - fix endpoint such that re-fechting is less impossible
  async function createLocation(location: CreateLocalLocation) {
    // Toevoeging is not required but needs to be in de request body
    const body: CreateApiLocation = {
      ...location,
      locatieType: location.locatieType?.value ?? null,
      toevoeging: location.toevoeging ?? '',
      huisNummer: location.huisNummer?.toString() ?? '',
    }

    const response = await axiosClient.request<LocationResponse>({
      url: '/afvalstroom/api/locatie',
      method: 'POST',
      data: body,
    })

    return decodeLocation(response.data.data)
  }

  return {
    locations,
    locationCategories,
    locationTypes,
    fetchLocationsForOrganization,
    fetchLocationCategories,
    createLocation,
  }
})
